// src/pages/PermissionsPage.js — Custom Permissions Management [marker: permissions-real v1]
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function PermissionsPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [definitions, setDefinitions] = useState({});
  const [roleDefaults, setRoleDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [presets, setPresets] = useState({});
  const [preset, setPreset] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/permissions/all-users'),
      api.get('/permissions/definitions'),
    ]).then(([usersRes, defsRes]) => {
      setUsers(usersRes.data);
      setDefinitions(defsRes.data.permissions);
      setRoleDefaults(defsRes.data.roleDefaults);
      setPresets(defsRes.data.presets || {});
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadUserPermissions = async (user) => {
    setSelectedUser(user);
    setPermissions(null);
    try {
      const res = await api.get(`/permissions/user/${user.id}`);
      setPermissions(res.data);
    } catch { toast.error('Failed to load permissions.'); }
  };

  const handleToggle = async (permKey, currentEffective) => {
    if (!selectedUser) return;
    setSaving(permKey);

    // Determine what the role default is
    const roleDefault = (roleDefaults[selectedUser.role] || []).includes(permKey);
    const newValue = !currentEffective;

    try {
      if (newValue === roleDefault) {
        // Revert to default — remove override
        await api.delete(`/permissions/user/${selectedUser.id}/${permKey}`);
      } else {
        // Set override
        await api.post(`/permissions/user/${selectedUser.id}`, { permissionKey: permKey, granted: newValue });
      }
      // Reload
      const res = await api.get(`/permissions/user/${selectedUser.id}`);
      setPermissions(res.data);
      // Update override count in users list
      setUsers(u => u.map(usr => usr.id === selectedUser.id ? { ...usr, override_count: Object.values(res.data.effectivePermissions).filter(p => p.override !== null).length } : usr));
      toast.success(newValue ? 'Permission granted.' : 'Permission revoked.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed.');
    } finally {
      setSaving(null);
    }
  };

  const applyPreset = async () => {
    if (!preset || !presets[preset]) return;
    if (!window.confirm(`Set ${selectedUser.first_name} up as "${presets[preset].label}"?\n\nEvery box will be set to match — anything not in that setup is turned off.`)) return;
    try {
      const r = await api.post(`/permissions/user/${selectedUser.id}/preset`, { preset });
      const res = await api.get(`/permissions/user/${selectedUser.id}`);
      setPermissions(res.data);
      setUsers(u => u.map(usr => usr.id === selectedUser.id ? { ...usr, override_count: Object.values(res.data.effectivePermissions).filter(p => p.override !== null).length } : usr));
      toast.success(r.data.message || 'Done.');
      setPreset('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
  };

  const handleResetAll = async () => {
    if (!window.confirm(`Reset all custom permissions for ${selectedUser.first_name}? They will revert to their role defaults.`)) return;
    try {
      await api.delete(`/permissions/user/${selectedUser.id}/all`);
      const res = await api.get(`/permissions/user/${selectedUser.id}`);
      setPermissions(res.data);
      setUsers(u => u.map(usr => usr.id === selectedUser.id ? { ...usr, override_count: 0 } : usr));
      toast.success('All overrides reset.');
    } catch { toast.error('Failed to reset.'); }
  };

  // Group permissions by category
  const categories = {};
  Object.entries(definitions).forEach(([key, def]) => {
    if (!categories[def.category]) categories[def.category] = [];
    categories[def.category].push({ key, ...def });
  });

  const roleColors = { super_admin: 'badge-navy', manager: 'badge-active', finance: 'badge-sent', staff: 'badge-draft' };

  if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 32, height: 32 }} /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Custom Permissions</h1>
          <p className="page-subtitle">What each staff member can see and do. Changes take effect within 30 seconds — the menu, the pages and the data all follow these boxes. Super admins always have everything.</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
        {/* User list */}
        <div className="card" style={{ height: 'fit-content' }}>
          <div className="card-header"><span className="card-title">Staff Users</span></div>
          <div>
            {users.filter(u => u.role !== 'super_admin').map(user => (
              <div
                key={user.id}
                onClick={() => loadUserPermissions(user)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--gray-100)',
                  background: selectedUser?.id === user.id ? 'var(--navy-ghost)' : 'white',
                  borderLeft: selectedUser?.id === user.id ? '3px solid var(--navy)' : '3px solid transparent',
                  opacity: user.is_active ? 1 : 0.5,
                }}
                onMouseOver={e => { if (selectedUser?.id !== user.id) e.currentTarget.style.background = 'var(--gray-50)'; }}
                onMouseOut={e => { if (selectedUser?.id !== user.id) e.currentTarget.style.background = 'white'; }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{user.first_name} {user.last_name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                  <span className={`badge ${roleColors[user.role]}`} style={{ fontSize: '0.65rem' }}>{user.role?.replace('_',' ')}</span>
                  {parseInt(user.override_count) > 0 && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--warning)', fontWeight: 600 }}>
                      {user.override_count} override{user.override_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Permission grid */}
        <div>
          {!selectedUser ? (
            <div className="card">
              <div className="empty-state" style={{ padding: 60 }}>
                <div className="empty-state-icon">👈</div>
                <h3>Select a staff user</h3>
                <p>Choose a user to view and customize their permissions</p>
              </div>
            </div>
          ) : !permissions ? (
            <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>
          ) : (
            <div>
              {/* User header */}
              <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>{selectedUser.first_name} {selectedUser.last_name}</span>
                    <span className={`badge ${roleColors[selectedUser.role]}`} style={{ marginLeft: 8 }}>{selectedUser.role?.replace('_',' ')}</span>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: 2 }}>{selectedUser.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                      {Object.values(permissions.effectivePermissions).filter(p => p.override !== null).length} custom overrides
                    </span>
                    {Object.values(permissions.effectivePermissions).some(p => p.override !== null) && (
                      <button className="btn btn-secondary btn-sm" onClick={handleResetAll}>Reset All to Defaults</button>
                    )}
                  </div>
                </div>
              </div>

              {/* [marker: permissions-real v1] ready-made setups */}
              <div className="card" style={{ marginBottom: 16, padding: '12px 20px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Quick setup:</span>
                <select className="form-select" style={{ maxWidth: 420 }} value={preset} onChange={e => setPreset(e.target.value)}>
                  <option value="">— choose a ready-made setup —</option>
                  {Object.entries(presets).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={!preset} onClick={applyPreset}>Apply</button>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Then fine-tune any box below.</span>
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--success)', display: 'inline-block' }} /> Granted
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--gray-200)', display: 'inline-block' }} /> Denied
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--warning)', display: 'inline-block', border: '2px solid var(--warning)' }} /> Custom override
                </span>
              </div>

              {/* Permission categories */}
              {Object.entries(categories).map(([category, perms]) => (
                <div key={category} className="card" style={{ marginBottom: 12 }}>
                  <div className="card-header" style={{ padding: '10px 16px' }}>
                    <span className="card-title" style={{ fontSize: '0.8rem' }}>{category}</span>
                  </div>
                  <div style={{ padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {perms.map(perm => {
                      const permState = permissions.effectivePermissions[perm.key];
                      if (!permState) return null;
                      const isCustom  = permState.override !== null;
                      const effective = permState.effective;
                      const isLoading = saving === perm.key;
                      const locked = perm.superOnly; // super admins only — can't be given to anyone else

                      return (
                        <div
                          key={perm.key}
                          onClick={() => !isLoading && !locked && handleToggle(perm.key, effective)}
                          title={locked ? 'Only super admins can do this.' : undefined}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 12px',
                            borderRadius: 'var(--radius)',
                            cursor: isLoading ? 'wait' : locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.55 : 1,
                            border: `1.5px solid ${isCustom ? 'var(--warning)' : effective ? 'var(--success)' : 'var(--gray-200)'}`,
                            background: effective ? (isCustom ? '#fffbeb' : 'var(--success-bg)') : 'var(--gray-50)',
                            transition: 'all 0.15s',
                            fontSize: '0.8rem',
                          }}
                        >
                          {isLoading ? (
                            <span className="spinner" style={{ width: 12, height: 12 }} />
                          ) : (
                            <span style={{ color: effective ? (isCustom ? 'var(--warning)' : 'var(--success)') : 'var(--gray-300)', fontSize: '0.9rem' }}>
                              {effective ? '✓' : '✗'}
                            </span>
                          )}
                          <span style={{ color: effective ? 'var(--gray-800)' : 'var(--gray-400)', fontWeight: isCustom ? 600 : 400 }}>
                            {perm.label}{locked ? ' (super admins only)' : ''}
                          </span>
                          {isCustom && (
                            <span style={{ fontSize: '0.65rem', background: 'var(--warning)', color: 'white', padding: '0 4px', borderRadius: 3, fontWeight: 700 }}>
                              CUSTOM
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
